import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/auth";
import { useCart } from "../../context/cart";

const CheckoutSuccess = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [reference, setReference] = useState("");
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const [auth, setAuth] = useAuth();
  const { clearCart } = useCart();

  const isLoggedIn = !!(auth && auth.token);

  useEffect(() => {
    const processOrder = async () => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      try {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");

        if (!sessionId) {
          toast.error("No session ID found");
          setIsProcessing(false);
          return;
        }
        setReference(sessionId);

        const token = localStorage.getItem("token");

        const { data } = await axios.get(
            `${process.env.REACT_APP_API}/checkout-success?session_id=${sessionId}`,
            { headers: { Authorization: token || "" } }
        );

        if (data.success) {
          toast.success("Order confirmed!");

          // Reset the cart everywhere at once: in-memory context state,
          // localStorage, and (for logged-in users) the server cart.
          await clearCart();

          if (data.isGuest) {
            setIsGuest(true);
            setShowAccountForm(true);
          }
        }

        setIsProcessing(false);
      } catch (error) {
        console.error("Error:", error);
        toast.error("We couldn't process your order. Please contact support.");
        setIsProcessing(false);
      }
    };

    processOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      setCreatingAccount(true);

      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");

      const { data } = await axios.post(
          `${process.env.REACT_APP_API}/create-account-from-guest`,
          { sessionId, password }
      );

      if (data.success) {
        toast.success("Account created!");
        await clearCart();
        localStorage.removeItem("guestCheckout");

        setAuth({ ...auth, currentUser: data.user, token: data.token });
        localStorage.setItem(
            "auth",
            JSON.stringify({ currentUser: data.user, token: data.token })
        );

        setTimeout(() => navigate("/orders"), 1000);
      }
    } catch (error) {
      const resp = error.response && error.response.data;
      const msg =
          (resp && (resp.message || resp.error)) || "Failed to create account";

      // Email already has an account -> send them to sign in.
      if (
          (resp && resp.code === "EMAIL_EXISTS") ||
          (error.response && error.response.status === 409)
      ) {
        toast.error(msg);
        localStorage.removeItem("guestCheckout");
        setTimeout(() => navigate("/login"), 1400);
        return;
      }

      toast.error(msg);
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleSkipAccount = () => {
    localStorage.removeItem("guestCheckout");
    navigate("/");
  };

  return (
      <div className="cs-wrap">
        <div className="cs-card">
          {isProcessing ? (
              <div className="cs-loading">
                <div className="cs-spinner" />
                <p>Confirming your order…</p>
              </div>
          ) : (
              <>
                <div className="cs-hero">
                  <svg className="cs-check" viewBox="0 0 88 88" aria-hidden="true">
                    <defs>
                      <linearGradient id="csGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#002E6A" />
                        <stop offset="100%" stopColor="#c70044" />
                      </linearGradient>
                    </defs>
                    <circle className="cs-check-ring" cx="44" cy="44" r="40" />
                    <path className="cs-check-mark" d="M26 45 l12 12 l24 -27" />
                  </svg>

                  <h2 className="cs-title">Order confirmed</h2>
                  <p className="cs-sub">
                    Thank you for your purchase. We'll ship within 24–48 hours and
                    email you tracking as soon as it's on the way.
                  </p>
                  {reference && (
                      <p className="cs-ref">
                        Reference&nbsp;
                        <span>{reference.slice(-12).toUpperCase()}</span>
                      </p>
                  )}
                </div>

                {showAccountForm && isGuest ? (
                    <div className="cs-form">
                      <h3>Create your account</h3>
                      <p className="cs-form-sub">
                        Save your details and track this order. It takes a second.
                      </p>

                      <form onSubmit={handleCreateAccount}>
                        <label className="cs-label">Password</label>
                        <input
                            type="password"
                            className="cs-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 6 characters"
                            required
                        />

                        <label className="cs-label">Confirm password</label>
                        <input
                            type="password"
                            className="cs-input"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Re-enter your password"
                            required
                        />

                        <button
                            type="submit"
                            className="cs-btn cs-btn-primary"
                            disabled={creatingAccount}
                        >
                          {creatingAccount ? "Creating account…" : "Create account"}
                        </button>
                        <button
                            type="button"
                            className="cs-btn cs-btn-ghost"
                            onClick={handleSkipAccount}
                            disabled={creatingAccount}
                        >
                          Skip for now
                        </button>
                      </form>
                    </div>
                ) : (
                    <div className="cs-actions">
                      {isLoggedIn && (
                          <button
                              className="cs-btn cs-btn-primary"
                              onClick={() => navigate("/orders")}
                          >
                            View my orders
                          </button>
                      )}
                      <Link to="/" className="cs-btn cs-btn-ghost">
                        Continue shopping
                      </Link>
                    </div>
                )}
              </>
          )}
        </div>

        <style>{`
        .cs-wrap {
          min-height: 70vh;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 56px 16px;
        }
        .cs-card {
          width: 100%;
          max-width: 520px;
          background: #fff;
          border: 1px solid #eef0f4;
          border-radius: 20px;
          box-shadow: 0 18px 50px rgba(0, 46, 106, 0.10);
          padding: 44px 36px;
        }
        .cs-hero { text-align: center; }
        .cs-check { width: 88px; height: 88px; margin-bottom: 18px; }
        .cs-check-ring {
          fill: none;
          stroke: url(#csGrad);
          stroke-width: 4;
          stroke-dasharray: 252;
          stroke-dashoffset: 252;
          animation: cs-ring 0.6s ease-out forwards;
        }
        .cs-check-mark {
          fill: none;
          stroke: url(#csGrad);
          stroke-width: 6;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 70;
          stroke-dashoffset: 70;
          animation: cs-mark 0.4s 0.5s ease-out forwards;
        }
        @keyframes cs-ring { to { stroke-dashoffset: 0; } }
        @keyframes cs-mark { to { stroke-dashoffset: 0; } }
        .cs-title {
          color: #002E6A;
          font-weight: 700;
          font-size: 1.7rem;
          margin: 0 0 10px;
          letter-spacing: -0.01em;
        }
        .cs-sub {
          color: #5b6473;
          font-size: 0.98rem;
          line-height: 1.6;
          margin: 0 auto;
          max-width: 380px;
        }
        .cs-ref {
          margin: 20px 0 0;
          font-size: 0.78rem;
          letter-spacing: 0.08em;
          color: #9aa1ad;
          text-transform: uppercase;
        }
        .cs-ref span { color: #c70044; font-weight: 600; }
        .cs-form {
          margin-top: 32px;
          padding-top: 28px;
          border-top: 1px solid #eef0f4;
        }
        .cs-form h3 { color: #002E6A; font-weight: 700; font-size: 1.15rem; margin: 0 0 6px; }
        .cs-form-sub { color: #5b6473; font-size: 0.9rem; margin: 0 0 20px; }
        .cs-label {
          display: block;
          font-size: 0.82rem;
          font-weight: 600;
          color: #3a4150;
          margin: 0 0 6px;
        }
        .cs-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d8dce3;
          border-radius: 10px;
          font-size: 0.95rem;
          margin-bottom: 16px;
          transition: border-color 0.15s, box-shadow 0.15s;
          outline: none;
        }
        .cs-input:focus {
          border-color: #002E6A;
          box-shadow: 0 0 0 3px rgba(0, 46, 106, 0.12);
        }
        .cs-actions { margin-top: 30px; display: grid; gap: 12px; }
        .cs-btn {
          display: block;
          width: 100%;
          text-align: center;
          padding: 13px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.95rem;
          border: 1px solid transparent;
          cursor: pointer;
          text-decoration: none;
          transition: transform 0.12s, box-shadow 0.15s, background 0.15s;
        }
        .cs-btn:active { transform: translateY(1px); }
        .cs-btn-primary {
          background: linear-gradient(135deg, #002E6A 0%, #c70044 100%);
          color: #fff;
          box-shadow: 0 6px 16px rgba(199, 0, 68, 0.22);
          margin-bottom: 4px;
        }
        .cs-btn-primary:disabled { opacity: 0.65; cursor: not-allowed; }
        .cs-btn-ghost {
          background: #fff;
          color: #002E6A;
          border-color: #d8dce3;
        }
        .cs-btn-ghost:hover { background: #f6f8fb; }
        .cs-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          color: #5b6473;
          padding: 24px 0;
        }
        .cs-spinner {
          width: 26px;
          height: 26px;
          border: 3px solid #e6e9ef;
          border-top-color: #c70044;
          border-radius: 50%;
          animation: cs-spin 0.7s linear infinite;
        }
        @keyframes cs-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .cs-check-ring, .cs-check-mark { animation: none; stroke-dashoffset: 0; }
          .cs-spinner { animation-duration: 1.2s; }
        }
      `}</style>
      </div>
  );
};

export default CheckoutSuccess;
